import axios from 'axios';

/**
 * Effektive Backend-Basis-URL.
 *
 * Reihenfolge:
 *   1. `window.location.origin` (so läuft das Frontend immer auf der Domain,
 *      auf der es selbst geladen wurde — Preview-Fork, Live-Domain, lokal).
 *   2. Build-Zeit-Fallback `REACT_APP_BACKEND_URL` (SSR / Tests ohne window).
 *
 * Das löst das Problem, dass die alte Preview-URL im Bundle einkompiliert
 * blieb und nach einem Fork unter neuer URL ins Leere zeigte.
 */
export const getBackendUrl = () => {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
    }
    return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;

/**
 * Extract a server-side error `detail` from an axios error, even when the
 * request used `responseType: 'blob'` (which makes axios deliver the error
 * body as a Blob rather than a parsed JSON object).
 */
export const extractApiError = async (err, fallback = 'Fehler') => {
    try {
        const data = err?.response?.data;
        const status = err?.response?.status;
        // eslint-disable-next-line no-console
        console.error('[extractApiError]', { status, dataType: typeof data, isBlob: data instanceof Blob, data });
        if (!data) return err?.message ? `${fallback} (${err.message})` : fallback;
        if (typeof data === 'string') {
            try { return JSON.parse(data).detail || fallback; } catch { return data || fallback; }
        }
        if (data instanceof Blob) {
            const text = await data.text();
            // eslint-disable-next-line no-console
            console.error('[extractApiError] blob text:', text);
            try { return JSON.parse(text).detail || fallback; } catch { return text || fallback; }
        }
        // Pydantic-v2-Validierungsfehler: detail ist ein Array von Objekten
        // { type, loc, msg, input, url }. Diese als String formatieren, sonst
        // crasht React, wenn sie roh an z.B. toast.error() übergeben werden.
        if (Array.isArray(data.detail)) {
            return data.detail
                .map(e => (e && (e.msg || e.message)) || JSON.stringify(e))
                .join(' · ') || fallback;
        }
        if (typeof data.detail === 'object' && data.detail) {
            return data.detail.msg || data.detail.message || JSON.stringify(data.detail);
        }
        return data.detail || data.message || fallback;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[extractApiError] unexpected:', e);
        return fallback;
    }
};

// Create axios instance with interceptors
const api = axios.create({
    baseURL: API_BASE,
    // KEINE statische Content-Type-Default-Konfiguration mehr — axios setzt für
    // JS-Objekte automatisch `application/json` und für FormData/FileBlob den
    // korrekten `multipart/form-data; boundary=…`-Header. Ein vorher hier
    // hartcodiertes `application/json` hat FormData-Uploads kaputt gemacht
    // (führte zu 422 mit Pydantic-detail-Array → React-Crash via toast.error).
});

// Request-Interceptor: stelle sicher, dass FormData OHNE überschriebenen
// Content-Type rausgeht, damit axios die Boundary korrekt anhängt.
api.interceptors.request.use((config) => {
    if (config.data instanceof FormData && config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
    }
    return config;
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = sessionStorage.getItem('bgpr_token') || localStorage.getItem('bgpr_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 responses — rollenspezifischer Redirect (Mitglieder → '/', Admin → '/login')
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            let role = null;
            try {
                const stored = localStorage.getItem('bgpr_user') || sessionStorage.getItem('bgpr_user');
                if (stored) role = JSON.parse(stored)?.role || null;
            } catch { /* */ }
            localStorage.removeItem('bgpr_token');
            localStorage.removeItem('bgpr_user');
            sessionStorage.removeItem('bgpr_token');
            sessionStorage.removeItem('bgpr_user');
            const target = (role === 'admin' || role === 'super_admin') ? '/login' : '/';
            // Nicht im Login-Flow umleiten (sonst Endlos-Schleife bei falschem Passwort).
            if (!window.location.pathname.startsWith('/login')) {
                window.location.href = target;
            }
        }
        return Promise.reject(error);
    }
);

// Auth
export const authApi = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    registerParticipant: (data) => api.post('/auth/participant/register', data),
    getMe: () => api.get('/auth/me'),
    getBadges: () => api.get('/auth/me/badges'),
    updateProfile: (data) => api.put('/auth/profile', data),
    forgotPassword: (email, hp_website) => api.post('/auth/forgot-password', { email, hp_website }),
    resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
    verifyEmail: (token) => api.post('/auth/verify-email', { token }),
    resendVerification: (email) => api.post('/auth/resend-verification', { email }),
    impersonate: (userId) => api.post(`/auth/impersonate/${userId}`),
    requestEmailChange: (newEmail) => api.post('/auth/email-change/request', { new_email: newEmail }),
    getEmailChangeStatus: () => api.get('/auth/email-change/status'),
    cancelEmailChange: () => api.delete('/auth/email-change/request'),
    confirmEmailChange: (token) => api.post('/auth/email-change/confirm', { token }),
};

// Instructors
export const instructorApi = {
    getAll: (archived = false) => api.get(`/instructors?archived=${archived}`),
    getCounts: () => api.get('/instructors/counts'),
    getOne: (id) => api.get(`/instructors/${id}`),
    getNeighbors: (id) => api.get(`/instructors/${id}/neighbors`),
    create: (data) => api.post('/instructors', data),
    update: (id, data) => api.put(`/instructors/${id}`, data),
    archive: (id) => api.put(`/instructors/${id}/archive`),
    restore: (id) => api.put(`/instructors/${id}/restore`),
    delete: (id, deleteDropbox = false) => api.delete(`/instructors/${id}${deleteDropbox ? '?delete_dropbox=true' : ''}`),
    wipeHistory: (id, deleteDropbox = false) => api.post(`/instructors/${id}/wipe-history${deleteDropbox ? '?delete_dropbox=true' : ''}`),
    getFullProfile: (id) => api.get(`/instructors/${id}/full-profile`),
};

// Admin — Datensätze zwischen Benutzern übertragen (Super-Admin only)
export const userDataTransferApi = {
    getSummary: (userId) => api.get(`/admin/users/${userId}/data-summary`),
    transfer: (payload) => api.post('/admin/users/transfer-data', payload),
};

// DSGVO — Konto-Löschung (Self-Service)
export const accountDeletionApi = {
    // Portal (eingeloggte Mitglieder)
    getMyRequest: () => api.get('/portal/account-deletion-request'),
    requestDeletion: (reason) => api.post('/portal/account-deletion-request', { reason }),
    withdrawMyRequest: () => api.delete('/portal/account-deletion-request'),
    // Admin
    listRequests: (status = 'pending') => api.get(`/admin/account-deletion-requests?status=${status}`),
    countPending: () => api.get('/admin/account-deletion-requests/count'),
    approve: (requestId, deleteDropbox = true) => api.post(`/admin/account-deletion-requests/${requestId}/approve`, { delete_dropbox: deleteDropbox }),
    reject: (requestId, reason) => api.post(`/admin/account-deletion-requests/${requestId}/reject`, { reason }),
};

// Licenses
export const licenseApi = {
    getAll: () => api.get('/licenses'),
    getMy: () => api.get('/licenses/my'),
    getByInstructor: (id) => api.get(`/licenses/instructor/${id}`),
    getNextNumber: () => api.get('/licenses/next-number'),
    create: (data) => api.post('/licenses', data),
    update: (id, data) => api.put(`/licenses/${id}`, data),
    deactivate: (id, reason) => api.put(`/licenses/${id}/deactivate`, { reason }),
    activate: (id) => api.put(`/licenses/${id}/activate`),
    extend: (id, validUntil) => api.put(`/licenses/${id}/extend?valid_until=${validUntil}`),
    renew: (id, data) => api.post(`/licenses/${id}/renew`, data),
    getHistory: (id) => api.get(`/licenses/${id}/history`),
    delete: (id) => api.delete(`/licenses/${id}`),
    generatePdf: (id) => api.post(`/licenses/${id}/generate-pdf`, {}, { responseType: 'blob' }),
    getPdf: (id) => api.get(`/licenses/${id}/pdf`, { responseType: 'blob' }),
    selfUpload: (fields, file) => {
        const fd = new FormData();
        fd.append('license_number', fields.license_number);
        fd.append('issued_date', fields.issued_date);
        fd.append('valid_until', fields.valid_until);
        fd.append('license_type', fields.license_type || 'Herzgruppenleiter');
        if (fields.original_issued_date) fd.append('original_issued_date', fields.original_issued_date);
        fd.append('document', file);
        return api.post('/licenses/my/self-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    listProofs: (id) => api.get(`/licenses/${id}/proofs`),
    uploadProof: (id, file, note = '') => {
        const fd = new FormData();
        fd.append('file', file);
        if (note) fd.append('note', note);
        return api.post(`/licenses/${id}/proofs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    downloadProof: (id, proofId) => api.get(`/licenses/${id}/proofs/${proofId}/download`, { responseType: 'blob' }),
    deleteProof: (id, proofId) => api.delete(`/licenses/${id}/proofs/${proofId}`),
    normalizeDates: () => api.post('/licenses/admin/normalize-dates'),
};

// Login-Page Settings (texts + hero image url)
export const loginPageApi = {
    getPublic: () => api.get('/settings/login-page/public'),
    get: () => api.get('/settings/login-page'),
    save: (data) => api.put('/settings/login-page', data),
    uploadHero: (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post('/settings/logos/login_hero', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteHero: () => api.delete('/settings/logos/login_hero'),
};

// Seminars
export const seminarApi = {
    getAll: () => api.get('/seminars'),
    getPublic: () => api.get('/seminars/public'),
    getPublicOne: (id) => api.get(`/seminars/public/${id}`),
    getOne: (id) => api.get(`/seminars/${id}`),
    create: (data) => api.post('/seminars', data),
    update: (id, data) => api.put(`/seminars/${id}`, data),
    togglePublish: (id) => api.put(`/seminars/${id}/publish`),
    delete: (id) => api.delete(`/seminars/${id}`),
    nextNumber: (year) => api.get(`/seminars/next-number${year ? `?year=${year}` : ''}`),
    getParticipants: (id) => api.get(`/seminars/${id}/participants`),
    batchInvoices: (id) => api.post(`/seminars/${id}/batch-invoices`),
    participantListFields: () => api.get('/seminars/_meta/participant-list-fields'),
    duplicate: (id) => api.post(`/seminars/${id}/duplicate`),
    markInHouse: (id, data) => api.post(`/seminars/${id}/mark-in-house`, data),
    getInHouseSignupUrl: (id) => api.get(`/seminars/${id}/in-house-signup-url`),
    generateInHouseInvoice: (id) => api.post(`/seminars/${id}/in-house-invoice`),
    listInHouseInvoices: (id) => api.get(`/seminars/${id}/in-house-invoices`),
    getDeleteImpact: (id) => api.get(`/seminars/${id}/delete-impact`),
    participantListPdf: (id, fields) => api.get(`/seminars/${id}/participant-list-pdf`, {
        responseType: 'blob',
        params: fields && fields.length ? { fields: fields.join(',') } : undefined,
    }),
    attendanceListPdf: (id) => api.get(`/seminars/${id}/attendance-list-pdf`, { responseType: 'blob' }),
    bulkCertificatesPdf: (id) => api.get(`/pdf/seminar/${id}/bulk-certificates`, { responseType: 'blob' }),
    confirmCourse: (id) => api.put(`/seminars/${id}/confirm-course`),
    backfillInvoices: (id) => api.post(`/seminars/${id}/backfill-invoices`),
    resyncTrainingRecords: (id) => api.post(`/seminars/${id}/resync-training-records`),
};

// Bookings
export const bookingApi = {
    getAll: () => api.get('/bookings'),
    getMy: () => api.get('/bookings/my'),
    getBySeminar: (id) => api.get(`/bookings/seminar/${id}`),
    getByInstructor: (id) => api.get(`/bookings/instructor/${id}`),
    create: (data) => api.post('/bookings', data),
    createParticipant: (seminarId, prerequisites, extras = {}) => api.post('/bookings/participant', { seminar_id: seminarId, prerequisites_confirmed: prerequisites || [], ...extras }),
    resendMeetingLink: (bookingId) => api.post(`/bookings/${bookingId}/resend-meeting-link`),
    setReferent: (bookingId, isReferent) => api.put(`/bookings/${bookingId}/referent`, { is_referent: isReferent }),
    getDetail: (bookingId) => api.get(`/bookings/${bookingId}/detail`),
    saveNote: (bookingId, note) => api.put(`/bookings/${bookingId}/note`, { note }),
    updatePrice: (bookingId, finalPrice) => api.put(`/bookings/${bookingId}/price`, { final_price: finalPrice }),
    createInvoice: (bookingId, sendEmail = false) => api.post(`/bookings/${bookingId}/create-invoice`, { send_email: sendEmail }),
    recordPayment: (bookingId, data) => api.post(`/bookings/${bookingId}/record-payment`, data),
    updateStatus: (id, status) => api.put(`/bookings/${id}/status?status=${status}`),
    markAttendance: (id, attended, date) => api.put(`/bookings/${id}/attendance?attended=${attended}${date ? `&date=${date}` : ''}`),
    downloadCertificate: (id, opts = {}) => api.get(`/pdf/certificate/${id}`, {
        responseType: 'blob',
        // Cache-Buster erzwingt frischen Response — auch wenn irgendwo ein Proxy
        // oder das Browser-Cache das GET zwischengespeichert hätte.
        params: opts.cacheBuster ? { _cb: opts.cacheBuster } : undefined,
    }),
    sendCertificateByEmail: (id) => api.post(`/pdf/certificate/${id}/send-email`),
    bulkSendCertificatesByEmail: (booking_ids) => api.post(`/pdf/certificate/bulk-send-email`, { booking_ids }),
    bulkRegenerateCertificates: (booking_ids) => api.post(`/pdf/certificate/bulk-regenerate`, { booking_ids }),
    downloadReceipt: (bookingId) => api.get(`/bookings/${bookingId}/receipt-pdf`, { responseType: 'blob' }),
    resendEmailLog: (logId) => api.post(`/email-logs/${logId}/resend`),
    cancelSelf: (bookingId) => api.post(`/bookings/${bookingId}/cancel`),
    promoteWaitingList: (bookingId) => api.post(`/bookings/${bookingId}/promote`),
    getSeminarWaitingList: (seminarId) => api.get(`/seminars/${seminarId}/waiting-list`),
    getCancellationQuote: (bookingId) => api.get(`/bookings/${bookingId}/cancellation-quote`),
    requestCancellation: (bookingId, data) => api.post(`/bookings/${bookingId}/request-cancellation`, data),
};

export const cancellationsApi = {
    listPending: () => api.get('/admin/cancellations/pending'),
    listRefundPending: () => api.get('/admin/cancellations/refund-pending'),
    summary: () => api.get('/admin/cancellations/summary'),
    approve: (id) => api.post(`/admin/cancellations/${id}/approve`),
    deny: (id, deny_reason) => api.post(`/admin/cancellations/${id}/deny`, { deny_reason }),
    markRefundPaid: (id, body = {}) => api.post(`/admin/cancellations/${id}/mark-refund-paid`, body),
};

// Invoices
export const invoiceApi = {
    getAll: () => api.get('/invoices'),
    getByInstructor: (id) => api.get(`/invoices/instructor/${id}`),
    create: (data) => api.post('/invoices', data),
    markPaid: (id, data) => api.put(`/invoices/${id}/pay`, data),
    revertPayment: (id, paymentId, reason = '') => api.post(`/invoices/${id}/revert-payment`, { payment_id: paymentId, reason }),
    downloadPdf: (id, kind) => api.get(`/invoices/${id}/pdf`, { params: kind ? { kind } : undefined, responseType: 'blob' }),
    sendEmail: (id, to) => api.post(`/invoices/${id}/send-email`, { to }),
    deleteCollective: (id) => api.delete(`/invoices/${id}/collective`),
    exportAccounting: (type, year, seminarId) => api.get('/invoices/export-pdf', {
        params: { type, year: year || 'all', seminar_id: seminarId || 'all' },
        responseType: 'blob',
    }),
    resyncReceiptsToDropbox: (invoiceIds) => api.post('/admin/invoices/resync-receipts-to-dropbox', { invoice_ids: invoiceIds || [] }),
};

export const expenseApi = {
    getAll: () => api.get('/expenses'),
    getBySeminar: (id) => api.get(`/expenses/seminar/${id}`),
    create: (data) => api.post('/expenses', data),
    update: (id, data) => api.put(`/expenses/${id}`, data),
    approve: (id, data) => api.put(`/expenses/${id}/approve`, data || {}),
    markPaid: (id, data) => api.put(`/expenses/${id}/pay`, data),
    upload: (id, file) => {
        const fd = new FormData(); fd.append('file', file);
        return api.post(`/expenses/${id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    delete: (id) => api.delete(`/expenses/${id}`),
    seminarAbrechnung: (id) => api.get(`/seminars/${id}/abrechnung-pdf`, { responseType: 'blob' }),
};

// Stats
export const statsApi = {
    getDashboard: () => api.get('/stats/dashboard'),
    getInstructorUE: (id) => api.get(`/stats/instructor/${id}/ue`),
};

// Settings
export const settingsApi = {
    getSmtp: () => api.get('/settings/smtp'),
    saveSmtp: (data) => api.post('/settings/smtp', data),
    testSmtp: (testEmail) => api.post('/settings/smtp/test', { test_email: testEmail }),
    getNotifications: () => api.get('/settings/notifications'),
    saveNotifications: (data) => api.post('/settings/notifications', data),
    getLicenseWarning: () => api.get('/settings/license-warning'),
    saveLicenseWarning: (data) => api.post('/settings/license-warning', data),
    getQualifications: () => api.get('/settings/qualifications'),
    saveQualifications: (data) => api.post('/settings/qualifications', data),
    getLicenseTypes: () => api.get('/settings/license-types'),
    saveLicenseTypes: (data) => api.post('/settings/license-types', data),
    createLicenseType: (data) => api.post('/settings/license-types', data),
    updateLicenseType: (id, data) => api.put(`/settings/license-types/${id}`, data),
    getSession: () => api.get('/settings/session'),
    saveSession: (data) => api.put('/settings/session', data),
    getSeminarCapacity: () => api.get('/settings/seminar-capacity'),
    saveSeminarCapacity: (data) => api.put('/settings/seminar-capacity', data),
    getMaintenance: () => api.get('/settings/maintenance'),
    saveMaintenance: (data) => api.put('/settings/maintenance', data),
    getCancellationRules: () => api.get('/settings/cancellation-rules'),
    saveCancellationRules: (data) => api.put('/settings/cancellation-rules', data),
    getCancellationReasons: () => api.get('/settings/cancellation-reasons'),
    saveCancellationReasons: (data) => api.put('/settings/cancellation-reasons', data),
    getRenewalRules: () => api.get('/settings/license-renewal-rules'),
    getRenewalRulesPublic: () => api.get('/settings/license-renewal-rules/public'),
    saveRenewalRules: (data) => api.post('/settings/license-renewal-rules', data),
    getLogos: () => api.get('/settings/logos'),
    uploadLogo: (type, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/settings/logos/${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    deleteLogo: (type) => api.delete(`/settings/logos/${type}`),
    getBank: () => api.get('/settings/bank'),
    saveBank: (data) => api.post('/settings/bank', data),
    getInvoiceSettings: () => api.get('/settings/invoice'),
    saveInvoiceSettings: (data) => api.post('/settings/invoice', data),
    getEmailSignature: () => api.get('/settings/email-signature'),
    saveEmailSignature: (data) => api.post('/settings/email-signature', data),
    testEmailSignature: (testEmail) => api.post('/settings/email-signature/test', { test_email: testEmail }),
    getAuditLog: (limit = 200) => api.get(`/admin/audit-log?limit=${limit}`),
    getAuditLogRecent: (limit = 200, category = 'all') =>
        api.get(`/system/audit-log-recent?limit=${limit}&category=${category}`),
    getPrerequisites: () => api.get('/settings/prerequisites'),
    getPrerequisitesPublic: () => api.get('/settings/prerequisites/public'),
    savePrerequisites: (data) => api.post('/settings/prerequisites', data),
    getReminderSettings: () => api.get('/settings/license-reminders'),
    saveReminderSettings: (data) => api.post('/settings/license-reminders', data),
    triggerReminders: () => api.post('/settings/license-reminders/send'),
    getPublicLicenseRenewalStatus: () => api.get('/public/license-renewal-status'),
    getRejectionReasons: () => api.get('/settings/rejection-reasons'),
    saveRejectionReasons: (data) => api.post('/settings/rejection-reasons', data),
};

// Templates
export const referentApi = {
    list: (activeOnly = false) => api.get('/referents', { params: { active_only: activeOnly } }),
    create: (data) => api.post('/referents', data),
    update: (id, data) => api.put(`/referents/${id}`, data),
    delete: (id) => api.delete(`/referents/${id}`),
};

export const templateApi = {
    getAll: () => api.get('/templates'),
    getOne: (id) => api.get(`/templates/${id}`),
    create: (data) => api.post('/templates', data),
    update: (id, data) => api.put(`/templates/${id}`, data),
    delete: (id) => api.delete(`/templates/${id}`),
    uploadAsset: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/template-assets/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    listAssets: () => api.get('/template-assets'),
    exportTemplate: (id) => api.get(`/templates/${id}/export`, { responseType: 'blob' }),
    importTemplate: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/templates/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    previewPdf: (id) => api.post(`/templates/${id}/preview`, {}, { responseType: 'blob' }),
    duplicate: (id, name) => api.post(`/templates/${id}/duplicate`, name ? { name } : {}),
    listVersions: (id) => api.get(`/templates/${id}/versions`),
    restoreVersion: (id, versionId) => api.post(`/templates/${id}/restore/${versionId}`),
};

// Seed
export const seedApi = {
    seed: () => api.post('/seed'),
};

export const emailApi = {
    listTemplates: (category) => api.get('/email-templates', { params: category ? { category } : {} }),
    getTemplate: (id) => api.get(`/email-templates/${id}`),
    previewTemplate: (id) => api.post(`/email-templates/${id}/preview`, {}),
    sendParticipantBulk: (data) => api.post('/email/send-participant-bulk', data),
    sendTest: (data) => api.post('/email/send-test', data),
    logsByUser: (userId, params = {}) => api.get(`/email-logs/by-user/${userId}`, { params }),
    myLogs: (params = {}) => api.get('/email-logs/me', { params }),
};

export const adminUserApi = {
    list: () => api.get('/admin/users'),
    create: (data) => api.post('/admin/users', data),
    update: (id, data) => api.put(`/admin/users/${id}`, data),
    setPassword: (id, data) => api.post(`/admin/users/${id}/set-password`, data),
    sendResetLink: (id) => api.post(`/admin/users/${id}/reset-link`),
    resetTwoFA: (id) => api.post(`/admin/users/${id}/2fa/disable`),
    unlockAccount: (id) => api.post(`/admin/users/${id}/unlock`),
    getPermissions: () => api.get('/admin/permissions'),
    setPermissions: (allowedTabs) => api.put('/admin/permissions', { allowed_tabs: allowedTabs }),
    getMyPermissions: () => api.get('/admin/my-permissions'),
};

export const twoFAApi = {
    status: () => api.get('/auth/2fa/status'),
    setupEmail: () => api.post('/auth/2fa/setup-email'),
    setupTotp: () => api.post('/auth/2fa/setup-totp'),
    confirmTotp: (code) => api.post('/auth/2fa/confirm-totp', { code }),
    disable: (password) => api.post('/auth/2fa/disable', { password }),
    disableMethod: (method, password) => api.post('/auth/2fa/disable-method', { method, password }),
    regenerate: () => api.post('/auth/2fa/regenerate-codes'),
    requestEmailOtpForChallenge: (challengeId) =>
        api.post('/auth/2fa-challenge/request-email', { challenge_id: challengeId }),
    verify: (challengeId, code, rememberDevice = false, deviceLabel = null) =>
        api.post('/auth/verify-2fa', {
            challenge_id: challengeId,
            code,
            remember_device: rememberDevice,
            device_label: deviceLabel,
        }),
    changePassword: (current, newPwd) => api.post('/auth/change-password', { current_password: current, new_password: newPwd }),
    // Pflicht-Setup-Flow für neu angelegte Admins (vor JWT-Ausgabe)
    forcedSetupBeginTotp: (setupId) => api.post('/auth/2fa-setup/begin-totp', { setup_id: setupId }),
    forcedSetupBeginEmail: (setupId) => api.post('/auth/2fa-setup/begin-email', { setup_id: setupId }),
    forcedSetupVerify: (setupId, method, code) =>
        api.post('/auth/2fa-setup/verify', { setup_id: setupId, method, code }),
};

export const faqApi = {
    portalList: () => api.get('/portal/faqs'),
    publicList: () => api.get('/portal/faqs/public'),
    adminList: () => api.get('/admin/faqs'),
    create: (data) => api.post('/admin/faqs', data),
    update: (id, data) => api.put(`/admin/faqs/${id}`, data),
    delete: (id) => api.delete(`/admin/faqs/${id}`),
    listCategories: () => api.get('/admin/faq-categories'),
    createCategory: (label) => api.post('/admin/faq-categories', { label }),
    updateCategory: (id, data) => api.put(`/admin/faq-categories/${id}`, data),
    deleteCategory: (id) => api.delete(`/admin/faq-categories/${id}`),
    reorderCategories: (order) => api.put('/admin/faq-categories/reorder', { order }),
    reorderFaqs: (categoryId, order) => api.put('/admin/faqs/reorder', { category_id: categoryId, order }),
};

// System
export const systemApi = {
    getStatus: () => api.get('/system/status'),
    createBackup: () => api.post('/system/backup', {}, { responseType: 'blob' }),
    restoreBackup: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/system/restore', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    triggerUpdate: () => api.post('/system/update'),
    performanceStats: () => api.get('/system/performance-stats'),
};

// System Health Dashboard
export const healthApi = {
    detailed: () => api.get('/health/detailed'),
    summary: () => api.get('/health/summary'),
    runAlertCheck: () => api.post('/health/run-alert-check'),
    getDiskConfig: () => api.get('/health/disk-config'),
    saveDiskConfig: (data) => api.post('/health/disk-config', data),
};

// Fonts
export const fontApi = {
    getAll: () => api.get('/fonts'),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/fonts/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    delete: (filename) => api.delete(`/fonts/${encodeURIComponent(filename)}`),
};

// Design Settings
export const designApi = {
    get: () => api.get('/design'),
    save: (settings) => api.post('/design', settings),
    saveColors: (colors) => api.post('/design/colors', colors),
    saveMeta: (meta) => api.post('/design/meta', meta),
    reset: () => api.post('/design/reset'),
};

// (PHP-Update-API entfernt – nicht mehr benötigt seit Wechsel auf Native Emergent Deployment)

// Pre-Deployment-Checkliste
export const deploymentApi = {
    readiness: () => api.get('/system/deployment-readiness'),
    cleanupTestData: () => api.post('/system/cleanup-test-data'),
    resetBookkeeping: (confirm) => api.post('/system/reset-bookkeeping', null, { params: { confirm } }),
    cleanupOrphans: () => api.post('/instructors/cleanup-orphans'),
    backupNow: () => api.post('/system/backup', null, { responseType: 'blob' }),
    testSmtp: (test_email) => api.post('/settings/smtp/test', { test_email }),
    setMaintenance: (data) => api.put('/settings/maintenance', data),
};

export const legalApi = {
    get: (type) => api.get(`/legal-texts/${type}`),
    update: (type, html_content) => api.put(`/legal-texts/${type}`, { html_content }),
};

// Öffentliche In-House-Firma-API (kein Auth erforderlich)
export const inHouseApi = {
    get: (token) => api.get(`/in-house/${token}`),
    setInvoiceAddress: (token, data) => api.put(`/in-house/${token}/invoice-address`, data),
    addParticipant: (token, data) => api.post(`/in-house/${token}/participants`, data),
    removeParticipant: (token, bookingId) => api.delete(`/in-house/${token}/participants/${bookingId}`),
    // Admin: aggregierte Firmen-Übersicht
    listCompanies: () => api.get('/companies/in-house'),
};

// Kurs-Absage & Umbuchung
export const courseCancellationApi = {
    getPreview: (seminarId) => api.get(`/seminars/${seminarId}/cancel-preview`),
    getRebookCandidates: (seminarId) => api.get(`/seminars/${seminarId}/rebook-candidates`),
    cancelCourse: (seminarId, data) => api.post(`/seminars/${seminarId}/cancel`, data),
    rebookSingle: (bookingId, targetSeminarId) => api.post(`/bookings/${bookingId}/rebook`, { target_seminar_id: targetSeminarId }),
    // Öffentliche Teilnehmer-Wahl
    getTokenInfo: (token) => api.get(`/rebook-tokens/${token}`),
    tokenRebook: (token, targetSeminarId) => api.post(`/rebook-tokens/${token}/rebook`, { target_seminar_id: targetSeminarId }),
    tokenRefund: (token, bankDetails) => api.post(`/rebook-tokens/${token}/refund`, bankDetails || {}),
};

// Admin-Tickets (Bug-/Fehler-Meldungen nach Rollout)
export const ticketsApi = {
    list: (status) => api.get('/tickets', { params: status ? { status } : {} }),
    countOpen: () => api.get('/tickets/count-open'),
    create: (data) => api.post('/tickets', data),
    update: (id, data) => api.patch(`/tickets/${id}`, data),
    remove: (id) => api.delete(`/tickets/${id}`),
    addComment: (id, body) => api.post(`/tickets/${id}/comments`, { body }),
    deleteComment: (id, commentId) => api.delete(`/tickets/${id}/comments/${commentId}`),
    // "Fehler melden" – öffentlich für jeden eingeloggten Nutzer (Teilnehmer + Admin).
    // formData muss title, description enthalten; optional screenshot (File), url_path, user_agent.
    report: (formData) => api.post('/tickets/report', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    attachmentUrl: (ticketId, attachmentId) => `${BACKEND_URL}/api/tickets/${ticketId}/attachments/${attachmentId}`,
};

// Ausgaben-Kategorien
export const expenseCategoriesApi = {
    list: (activeOnly = false) => api.get('/expense-categories', { params: { active_only: activeOnly } }),
    create: (data) => api.post('/expense-categories', data),
    update: (id, data) => api.patch(`/expense-categories/${id}`, data),
    remove: (id) => api.delete(`/expense-categories/${id}`),
};

export const trainingApi = {
    getRecords: () => api.get('/training-records'),
    addRecord: (data, file) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('date_issued', data.date_issued);
        formData.append('ue_count', String(data.ue_count));
        formData.append('record_type', data.record_type || 'regular');
        if (file) formData.append('document', file);
        return api.post('/training-records', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    deleteRecord: (id) => api.delete(`/training-records/${id}`),
    getDocument: (id) => api.get(`/training-records/${id}/document`, { responseType: 'blob' }),
    adminEdit: (id, data) => api.put(`/training-records/admin/${id}`, data),
    adminApprove: (id, data) => api.post(`/training-records/admin/${id}/approve`, data || {}),
    adminReject: (id, data) => api.post(`/training-records/admin/${id}/reject`, data),
    adminUnconsume: (id) => api.post(`/training-records/admin/${id}/unconsume`),
};

export const renewalApi = {
    getMyRenewals: () => api.get('/license-renewals'),
    getMyRequirements: () => api.get('/license-renewals/my-requirements'),
    markMyRenewalsSeen: () => api.post('/license-renewals/my/mark-seen'),
    requestRenewal: (payload) => api.post('/license-renewals', typeof payload === 'string' ? { notes: payload } : payload),
    uploadExistingLicense: (file) => {
        const fd = new FormData();
        fd.append('document', file);
        return api.post('/license-renewals/upload-existing-license', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    updateStatus: (id, data) => api.put(`/license-renewals/admin/${id}`, data),
    getReview: (id) => api.get(`/license-renewals/admin/${id}/review`),
};

export const dropboxApi = {
    getSettings: () => api.get('/dropbox/settings'),
    saveSettings: (data) => api.put('/dropbox/settings', data),
    testConnection: () => api.post('/dropbox/test'),
    authStart: () => api.post('/dropbox/auth/start'),
    authFinish: (code) => api.post('/dropbox/auth/finish', { code }),
    disconnect: () => api.post('/dropbox/disconnect'),
    listFolders: (path) => api.get(`/dropbox/folders?path=${encodeURIComponent(path || '')}`),
    syncTrainingRecord: (recordId) => api.post(`/dropbox/upload/training-record/${recordId}`),
    uploadLicense: (licenseId, file) => {
        const fd = new FormData();
        fd.append('document', file);
        return api.post(`/dropbox/upload/license/${licenseId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    uploadCertificate: (bookingId, file) => {
        const fd = new FormData();
        fd.append('document', file);
        return api.post(`/dropbox/upload/certificate/${bookingId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    download: (path) => api.get(`/dropbox/download?path=${encodeURIComponent(path)}`, { responseType: 'blob' }),
};


export default api;
